import { useState } from 'react'
import { useFetchData } from '../hooks/useFetchData'
import { Users, Loader, AlertCircle, CheckCircle } from 'lucide-react'

export default function Interview({ state, updateState }) {
  const { data, loading, error } = useFetchData('/data/interview_questions.json')
  const [selectedCategory, setSelectedCategory] = useState('personal')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [response, setResponse] = useState('')
  const [responses, setResponses] = useState([])
  const [showIdeal, setShowIdeal] = useState(false)
  const [sessionActive, setSessionActive] = useState(false)

  const categories = data?.categories || {}
  const categoryQuestions = categories[selectedCategory] || []
  const currentQuestion = categoryQuestions[currentIndex]

  const handleStartSession = () => {
    setSessionActive(true)
    setResponses([])
    setCurrentIndex(0)
    setResponse('')
    setShowIdeal(false)
  }

  const handleSubmitResponse = () => {
    const newResponse = {
      questionId: currentQuestion.id,
      question: currentQuestion.question,
      response,
      timestamp: new Date().toISOString(),
    }

    setResponses([...responses, newResponse])
    setShowIdeal(true)
  }

  const handleNext = () => {
    if (currentIndex < categoryQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setResponse('')
      setShowIdeal(false)
    } else {
      handleEndSession()
    }
  }

  const handleEndSession = () => {
    // Save session
    updateState('progress.interview.sessions', [
      ...(state.progress.interview?.sessions || []),
      {
        category: selectedCategory,
        responses,
        completedAt: new Date().toISOString(),
      },
    ])

    setSessionActive(false)
    setResponses([])
    setCurrentIndex(0)
    setResponse('')
    setShowIdeal(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading questions...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading Questions</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!sessionActive) {
    return (
      <div className="space-y-6 animate-slide-in max-w-4xl mx-auto">
        <div className="flex items-center">
          <Users className="w-8 h-8 text-primary-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold">Mock Interview</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Practice common SSB interview questions
            </p>
          </div>
        </div>

        <div className="card bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500">
          <h3 className="font-bold mb-2">Interview Tips</h3>
          <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            <li>• Be honest and authentic in your responses</li>
            <li>• Structure your answers clearly (STAR format when applicable)</li>
            <li>• Show awareness of current affairs and defence matters</li>
            <li>• Demonstrate leadership qualities and values</li>
            <li>• Maintain eye contact and confident body language</li>
          </ul>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold mb-4">Select Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.keys(categories).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`p-4 border-2 rounded-lg capitalize transition-all ${
                  selectedCategory === category
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
                }`}
              >
                <div className="font-semibold">{category.replace('_', ' ')}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {categories[category].length} questions
                </div>
              </button>
            ))}
          </div>

          <button onClick={handleStartSession} className="btn-primary w-full mt-6">
            Start Interview Session
          </button>
        </div>

        {/* Session History */}
        {state.progress.interview?.sessions && state.progress.interview.sessions.length > 0 && (
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Recent Sessions</h2>
            <div className="space-y-3">
              {state.progress.interview.sessions.slice(-5).reverse().map((session, index) => (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-semibold capitalize">
                        {session.category.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        {new Date(session.completedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {session.responses.length} questions
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-slide-in max-w-4xl mx-auto">
      <div className="card bg-primary-50 dark:bg-primary-900/20">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold capitalize">{selectedCategory.replace('_', ' ')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Question {currentIndex + 1} of {categoryQuestions.length}
            </p>
          </div>
          <button onClick={handleEndSession} className="btn-secondary">
            End Session
          </button>
        </div>

        <div className="progress-bar mt-4">
          <div
            className="progress-fill"
            style={{ width: `${((currentIndex + 1) / categoryQuestions.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="card">
        <h3 className="text-xl font-semibold mb-6 text-primary-600 dark:text-primary-400">
          {currentQuestion?.question}
        </h3>

        <textarea
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          placeholder="Type your response here..."
          className="textarea-field mb-4"
          rows="8"
          disabled={showIdeal}
        />

        {!showIdeal && (
          <button
            onClick={handleSubmitResponse}
            disabled={!response.trim()}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Response
          </button>
        )}
      </div>

      {showIdeal && currentQuestion && (
        <div className="space-y-4 animate-slide-in">
          <div className="card bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500">
            <div className="flex items-center mb-3">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="font-bold">Response Recorded</h3>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              <strong>Your response:</strong> {response}
            </p>
          </div>

          <div className="card">
            <h3 className="font-bold text-lg mb-3">Ideal Answer</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {currentQuestion.ideal_answer}
            </p>

            {currentQuestion.tips && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Tips:</h4>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  {currentQuestion.tips.map((tip, index) => (
                    <li key={index}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <button onClick={handleNext} className="btn-primary w-full">
            {currentIndex < categoryQuestions.length - 1 ? 'Next Question' : 'Finish Session'}
          </button>
        </div>
      )}
    </div>
  )
}
